import { Box, Button, Flex, Text } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { useParams } from "react-router"
import { URLForwardSlashRestore, componentMinHeight } from "../../common/commonConstants"
import { Colors } from "../../theme/colors"
import { useProductServicesByCodeQuery, type ProductService } from "../../type-and-hooks"
import { ErrorMessage } from "../commons/error.main"
import { Loading } from "../commons/loader.main"
import { MS70CDocumentation } from "./ms70c-documentation"
import { MS70RDocumentation } from "./ms70r-documentation"
import { ProductTableContent } from "./product-info.support"
import { UtilityCollarDocumentation } from "./utility-collar"

interface ProductDocumentationTableProps {
  searchQuery?: string
}

const rowMatchesSearch = (row: ProductService, query: string): boolean => {
  const q = query.toLowerCase()
  return (
    (row.service_name ?? "").toLowerCase().includes(q) ||
    (row.service_size ?? "").toLowerCase().includes(q) ||
    (row.system_frl ?? "").toLowerCase().includes(q) ||
    (row.rep_grp ?? []).some(r => (r?.report_name ?? "").toLowerCase().includes(q))
  )
}

export const ProductDocumentationTable = ({ searchQuery = "" }: ProductDocumentationTableProps): JSX.Element => {
  const { id: productId } = useParams<{ id: string }>()
  const id = URLForwardSlashRestore(productId)
  const { loading, error, data } = useProductServicesByCodeQuery({ variables: { productCode: id ?? "" }, fetchPolicy: "cache-and-network" })

  const isMS70C = id?.toUpperCase() === "MS70C"
  const isMS70R = id?.toUpperCase() === "MS70R"
  const isUtilityCollar = id?.toUpperCase() === "UTILITY-COLLAR"
  const isStaticProductPage = isMS70C || isMS70R || isUtilityCollar

  // expandedMap keys: "${pgIdx}-${egIdx}" → true/false
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({})

  const isSearchActive = searchQuery.trim().length > 0

  // Recalculate expand map whenever search or data changes
  useEffect(() => {
    if (!data) return
    if (isSearchActive) {
      const newMap: Record<string, boolean> = {}
      data.productServicesByCode?.data?.forEach((pg, pgIdx) => {
        pg?.element_grp?.forEach((eg, egIdx) => {
          const hasMatch = (eg?.services_grp as ProductService[] ?? []).some(row =>
            rowMatchesSearch(row, searchQuery)
          )
          newMap[`${pgIdx}-${egIdx}`] = hasMatch
        })
      })
      setExpandedMap(newMap)
    } else {
      const newMap: Record<string, boolean> = {}
      data.productServicesByCode?.data?.forEach((pg, pgIdx) => {
        pg?.element_grp?.forEach((_, egIdx) => {
          newMap[`${pgIdx}-${egIdx}`] = true
        })
      })
      setExpandedMap(newMap)
    }
  }, [searchQuery, data])

  const expandAll = (): void => {
    const newMap: Record<string, boolean> = {}
    data?.productServicesByCode?.data?.forEach((pg, pgIdx) => {
      pg?.element_grp?.forEach((_, egIdx) => {
        newMap[`${pgIdx}-${egIdx}`] = true
      })
    })
    setExpandedMap(newMap)
  }

  const collapseAll = (): void => { setExpandedMap({}) }

  const hasRestrictedReport = (data?.productServicesByCode?.data ?? []).some(productGroup =>
    (productGroup?.element_grp ?? []).some(elementGroup => {
      const services = isSearchActive
        ? (elementGroup?.services_grp as ProductService[] ?? []).filter(row => rowMatchesSearch(row, searchQuery))
        : (elementGroup?.services_grp as ProductService[] ?? [])
      return services.some(row => (row.rep_grp ?? []).some(r => r?.is_public === false))
    })
  )

  const toggle = (key: string): void => {
    setExpandedMap(prev => ({ ...prev, [key]: !(prev[key] ?? false) }))
  }

  return (
    <>
      <Loading loading={loading} />
      {error && <ErrorMessage error={"Failed to load: Product Info Table."} primaryAction={false} primaryActionText={""} />}

      {isMS70C && <MS70CDocumentation />}
      {isMS70R && <MS70RDocumentation />}
      {isUtilityCollar && <UtilityCollarDocumentation />}

      {!isStaticProductPage && data && (
        <Box minH={componentMinHeight} minW="100%" overflow="auto">
          {!isSearchActive && (
            <Flex align="center" mb="8px">
              <Box
                as="button"
                fontSize="12px"
                color="#861F41"
                fontWeight={400}
                px="8px"
                py="2px"
                borderRadius="4px"
                _hover={{ bg: "#f5e8ec", textDecoration: "underline" }}
                onClick={expandAll}
              >
                Expand all
              </Box>
              <Text fontSize="12px" color="#ccc" userSelect="none" mx="2px">|</Text>
              <Box
                as="button"
                fontSize="12px"
                color="#861F41"
                fontWeight={400}
                px="8px"
                py="2px"
                borderRadius="4px"
                _hover={{ bg: "#f5e8ec", textDecoration: "underline" }}
                onClick={collapseAll}
              >
                Collapse all
              </Box>
            </Flex>
          )}

          {data?.productServicesByCode?.data?.map((productGroup, pgIdx) => (
            <Box key={`${productGroup?.application ?? ""}${pgIdx}`}>
              {productGroup?.element_grp?.map((elementGroup, egIdx) => {
                const key = `${pgIdx}-${egIdx}`
                const filteredServices = isSearchActive
                  ? (elementGroup?.services_grp as ProductService[] ?? []).filter(row =>
                      rowMatchesSearch(row, searchQuery)
                    )
                  : (elementGroup?.services_grp as ProductService[] ?? [])

                if (isSearchActive && filteredServices.length === 0) return null

                return (
                  <React.Fragment key={`${egIdx}_${elementGroup?.element_name ?? ""}`}>
                    <ProductTableContent
                      hideHeading={Boolean(egIdx)}
                      headings={data.productServicesByCode?.header as string[]}
                      title={elementGroup?.element_name ?? ""}
                      data={filteredServices}
                      applicationPrefix={productGroup?.application}
                      isExpanded={expandedMap[key] ?? false}
                      onToggle={() => { toggle(key) }}
                    />
                  </React.Fragment>
                )
              })}
            </Box>
          ))}

          {hasRestrictedReport && (
            <Text fontSize="12px" color={Colors.Secondary.c2} mt="2">
              * Please contact our customer support for the report.
            </Text>
          )}
        </Box>
      )}
    </>
  )
}
